import RefreshIcon from "@mui/icons-material/CachedRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import ReplayIcon from "@mui/icons-material/ReplayRounded";
import { Box, Button, Divider, Drawer, useMediaQuery, type Theme } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import HorizontalSplitPane from "@entrilia/shared/components/HorizontalSplitPane";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import NoAccessPlaceholder from "@entrilia/shared/components/NoAccessPlaceholder";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../api/adminApi";
import type {
  UploadWorkflowAttachmentResult,
  Workflow,
  WorkflowActivity,
  WorkflowFileInfo,
} from "../../../../../api/types/workflowTypes";
import useClientContext from "../../../../../context/useClientContext";
import RerunIcon from "@entrilia/shared/icons/RerunIcon";
import TokenIcon from "@entrilia/shared/icons/TokenIcon";
import { pageRoutes } from "../../../../../routes";
import GeneralPageHeader from "../../../../common/GeneralPageHeader";
import CancelWorkflowDialog from "./CancelWorkflowDialog";
import RestartWorkflowDialog from "./RestartWorkflowDialog";
import RetryWorkflowDialog from "./RetryWorkflowDialog";
import WorkflowDetailPanel from "./WorkflowDetailPanel";
import type { FilePreviewState, OpenFieldCitation, WorkflowStep } from "./workflowDetailsHelpers";
import { getCurrentWorkflowStepIndex, getWorkflowRunHeaderActions } from "./workflowDetailsHelpers";
import WorkflowFilePreview from "./WorkflowFilePreview";
import WorkflowMasterPanel from "./WorkflowMasterPanel";
import { WorkflowRunDetailsContextProvider } from "./WorkflowRunDetailsContext";
import WorkflowRunDetailsPageTitle from "./WorkflowRunDetailsPageTitle";
import WorkflowTokenUsageDialog from "./WorkflowTokenUsageDialog";
import { getWorkflowTokenUsage } from "./workflowTokenUsageHelper";

const getWorkflowEtag = withErrorHandling(adminApi.getWorkflowEtag);

const WorkflowRunDetailsPage = () => {
  usePageTitle("Workflow Run");

  const isSmallerScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));

  const { id, templateId } = useParams();
  const { clientCode, hasPermissions } = useClientContext();
  const { sendNotification } = useNotificationContext();

  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const isUserStepSelectionRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showTokenUsagePopup, setShowTokenUsagePopup] = useState(false);
  const [showCancelWorkflowDialog, setShowCancelWorkflowDialog] = useState(false);
  const [showRestartWorkflowDialog, setShowRestartWorkflowDialog] = useState(false);
  const [showRetryWorkflowDialog, setShowRetryWorkflowDialog] = useState(false);
  const [filePreview, setFilePreview] = useState<FilePreviewState>();

  const getWorkflowDetails = useCallback(() => adminApi.getWorkflowDetails(defined(id)), [id]);
  const getWorkflowGroup = useCallback(
    () =>
      adminApi.getWorkflowGroup(templateId ?? "").catch((error: unknown) => {
        logError(error, "[WorkflowRunDetailsPage] getWorkflowGroup");
        throw error;
      }),
    [templateId]
  );

  const etagRef = useRef<string | undefined>(undefined);

  const [workflowDetails, fetchError, { fetch, isFetching, setData, cancel }] = useFetch(getWorkflowDetails, {
    onSetData: (data) => {
      etagRef.current = data.workflow.etag;

      if (!isUserStepSelectionRef.current) {
        setSelectedStepIndex(getCurrentWorkflowStepIndex(data));
      }
    },
  });

  const [workflowGroup] = useFetch(getWorkflowGroup, { disabled: !templateId || !hasPermissions(["ManageWorkflows"]) });

  const tokenUsage = useMemo(
    () => (workflowDetails === undefined ? undefined : getWorkflowTokenUsage(workflowDetails.workflow.activities)),
    [workflowDetails]
  );

  const steps = useMemo<WorkflowStep[]>(() => {
    if (!workflowDetails) {
      return [];
    }

    const { workflow } = workflowDetails;
    const hasSummary = workflow.progress.entries.length > 0;

    return [
      ...workflow.activities.map((activity) => ({ type: "activity" as const, activity })),
      ...(hasSummary ? [{ type: "summary" as const }] : []),
    ];
  }, [workflowDetails]);

  const isEtagPollDisabled = useMemo(
    () => workflowDetails?.workflow.currentActivity === undefined || workflowDetails.workflow.status !== "InProgress",
    [workflowDetails]
  );

  const handleEtagPoll = useCallback(async () => {
    const [etag, error] = await getWorkflowEtag(defined(id));
    if (error) {
      logError(error, "[WorkflowRunDetailsPage] getWorkflowEtag");
      return;
    }

    if (etagRef.current !== undefined && etag !== etagRef.current) {
      if (isFetching) {
        return;
      }

      await fetch();
    }

    etagRef.current = etag;
  }, [fetch, id, isFetching]);

  useSetInterval(handleEtagPoll, 3_000, { disabled: isEtagPollDisabled });

  const handleSelectStep = useCallback((index: number) => {
    setSelectedStepIndex(index);
    isUserStepSelectionRef.current = true;
  }, []);

  const handleViewCitations = useCallback(
    (activityId: string, documentName: string, fieldId?: string, selectedCitationId?: string) =>
      setFilePreview({ type: "citations", activityId, documentName, fieldId, selectedCitationId }),
    []
  );

  const openFieldCitation = useMemo<OpenFieldCitation | undefined>(
    () =>
      filePreview?.type === "citations" && filePreview.fieldId
        ? { activityId: filePreview.activityId, fieldId: filePreview.fieldId }
        : undefined,
    [filePreview]
  );

  if (fetchError) {
    if (fetchError.code === 403) {
      return <NoAccessPlaceholder text="You do not have permission to view this workflow." />;
    }

    logError(fetchError, "[WorkflowRunDetailsPage] getWorkflowDetails");
    return <DataLoadingFailed title="Could not load workflow details" />;
  }

  if (workflowDetails === undefined) {
    return <InlineLoader />;
  }

  const handleActionExecuted = () => {
    setTimeout(() => {
      fetch();
    }, 500);
  };

  const handleRefreshClick = async () => {
    setRefreshing(true);
    await fetch();
    setRefreshing(false);
  };

  const handleWorkflowUpdated = (updatedWorkflow: Workflow) => {
    setData((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        workflow: updatedWorkflow,
      };
    });
  };

  const handleActivitySaved = (updatedActivity: WorkflowActivity) => {
    setData((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedActivities = prev.workflow.activities.map((activity) =>
        activity.workflowActivityId === updatedActivity.workflowActivityId ? updatedActivity : { ...activity }
      );

      return {
        ...prev,
        workflow: {
          ...prev.workflow,
          activities: updatedActivities,
        },
      };
    });
  };

  const canManageFiles = workflowDetails.canCurrentUserTriggerActions && workflowDetails.workflow.requiresUserAction;
  const headerActions = getWorkflowRunHeaderActions(workflowDetails);

  const handleFileUploaded = (result: UploadWorkflowAttachmentResult) => {
    fetch();

    const { isReplacement, wasReevaluated } = result;

    if (isReplacement && wasReevaluated) {
      sendNotification("File replaced successfully. AI agent is re-extracting data from the updated document.");
    } else if (isReplacement) {
      sendNotification("File replaced successfully.");
    } else if (wasReevaluated) {
      sendNotification("File uploaded successfully. AI agent is re-processing the data.");
    } else {
      sendNotification("File uploaded successfully.");
    }
  };

  const handleFileDeleted = (updatedWorkflow: Workflow, fileCatalogId: string, fileName: string) => {
    setFilePreview((current) => {
      if (current?.type === "document" && current.attachment.fileCatalogId === fileCatalogId) {
        return undefined;
      }
      if (current?.type === "citations" && current.documentName === fileName) {
        return undefined;
      }
      return current;
    });
    cancel();
    setData((prev) => (prev ? { ...prev, workflow: updatedWorkflow } : prev));
    etagRef.current = updatedWorkflow.etag;
    sendNotification("Attachment deleted.");
  };

  const handleOpenDocumentPreview = (attachment: WorkflowFileInfo) => {
    setFilePreview({ type: "document", attachment });
  };

  const handleOpenEmailPreview = () => {
    setFilePreview({ type: "email", subject: workflowDetails.triggerInboxItem?.subject });
  };

  return (
    <WorkflowRunDetailsContextProvider
      workflowDetails={workflowDetails}
      steps={steps}
      selectedStepIndex={selectedStepIndex}
      selectStep={handleSelectStep}
      openFieldCitation={openFieldCitation}
      viewCitations={handleViewCitations}
      currentTemplateVersionId={
        workflowGroup?.templateId === templateId ? workflowGroup?.latestTemplateVersionId : undefined
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        <GeneralPageHeader
          TitleComponent={<WorkflowRunDetailsPageTitle workflowDetails={workflowDetails} />}
          showDefaultBackButtonTo={`/${clientCode}/${pageRoutes.workflows}/${pageRoutes.workflowRuns}/${templateId}`}
        >
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            loading={refreshing}
            onClick={handleRefreshClick}
          >
            Refresh
          </Button>
          {headerActions.showRetry && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ReplayIcon />}
              onClick={() => setShowRetryWorkflowDialog(true)}
            >
              Retry
            </Button>
          )}
          {headerActions.showRestart && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RerunIcon />}
              disabled={!headerActions.canRestart}
              onClick={() => setShowRestartWorkflowDialog(true)}
            >
              Restart
            </Button>
          )}
          {headerActions.showCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              disabled={!headerActions.canCancel}
              onClick={() => setShowCancelWorkflowDialog(true)}
            >
              Cancel
            </Button>
          )}
          <ActionsMenuButton
            items={[
              {
                label: "Token Usage",
                icon: <TokenIcon color="secondary" />,
                onClick: () => setShowTokenUsagePopup(true),
                disabled: tokenUsage === undefined || tokenUsage.groups.length === 0,
              },
            ]}
          />
        </GeneralPageHeader>

        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <WorkflowMasterPanel
            onOpenAttachmentPreview={handleOpenDocumentPreview}
            onOpenEmailPreview={handleOpenEmailPreview}
            onFileUploaded={handleFileUploaded}
            onFileDeleted={handleFileDeleted}
            canManageFiles={canManageFiles}
          />

          <Divider orientation="vertical" flexItem />

          {!filePreview && (
            <WorkflowDetailPanel
              onActionExecuted={handleActionExecuted}
              onWorkflowUpdated={handleWorkflowUpdated}
              onActivitySaved={handleActivitySaved}
            />
          )}

          {filePreview && isSmallerScreen && (
            <>
              <WorkflowDetailPanel
                onActionExecuted={handleActionExecuted}
                onWorkflowUpdated={handleWorkflowUpdated}
                onActivitySaved={handleActivitySaved}
              />
              <Drawer
                anchor="right"
                open
                onClose={() => setFilePreview(undefined)}
                sx={{ "& .MuiDrawer-paper": { width: "52rem" } }}
              >
                <WorkflowFilePreview
                  filePreview={filePreview}
                  workflow={workflowDetails.workflow}
                  onClose={() => setFilePreview(undefined)}
                />
              </Drawer>
            </>
          )}

          {filePreview && !isSmallerScreen && (
            <HorizontalSplitPane
              leftPane={
                <WorkflowDetailPanel
                  onActionExecuted={handleActionExecuted}
                  onWorkflowUpdated={handleWorkflowUpdated}
                  onActivitySaved={handleActivitySaved}
                />
              }
              rightPane={
                <WorkflowFilePreview
                  filePreview={filePreview}
                  workflow={workflowDetails.workflow}
                  onClose={() => setFilePreview(undefined)}
                />
              }
              initialRightWidth={840}
              minLeftWidth={400}
              minRightWidth={800}
              containerSx={{ flex: 1, minWidth: 0 }}
              leftPaneSx={{ display: "flex" }}
            />
          )}
        </Box>

        {tokenUsage && (
          <WorkflowTokenUsageDialog
            open={showTokenUsagePopup}
            onClose={() => setShowTokenUsagePopup(false)}
            tokenUsage={tokenUsage}
          />
        )}

        {showCancelWorkflowDialog && (
          <CancelWorkflowDialog
            workflowId={workflowDetails.workflow.id}
            workflowType={workflowDetails.workflow.type}
            onClose={() => setShowCancelWorkflowDialog(false)}
            onCancelled={handleActionExecuted}
          />
        )}

        {showRetryWorkflowDialog && (
          <RetryWorkflowDialog
            workflowId={workflowDetails.workflow.id}
            workflowType={workflowDetails.workflow.type}
            onClose={() => setShowRetryWorkflowDialog(false)}
            onRetried={handleActionExecuted}
          />
        )}

        {showRestartWorkflowDialog && (
          <RestartWorkflowDialog
            workflowId={workflowDetails.workflow.id}
            templateId={defined(templateId)}
            workflowType={workflowDetails.workflow.type}
            onClose={() => setShowRestartWorkflowDialog(false)}
          />
        )}
      </Box>
    </WorkflowRunDetailsContextProvider>
  );
};

export default WorkflowRunDetailsPage;
